import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchECACReport, resetECACReport, selectECACReportData } from 'redux/slices/sla-reports/ecac-report';
import { fetchExternalCallPointReport, resetExternalCallPointReport, selectExternalCallPointReportData } from 'redux/slices/sla-reports/external-call-point-report';
import { fetchBoardingSla, resetBoardingSla, selectBoardingSlaData } from 'redux/slices/sla-reports/boarding-sla';
import { fetchInboundSla, resetInboundSla, selectInboundSlaData } from 'redux/slices/sla-reports/inbound-sla';
import { fetchTerminalReceptionReport, resetTerminalReception, selectTerminalReceptionData } from 'redux/slices/sla-reports/terminal-reception';
import { fetchTransferSla, resetTransferSla, selectTransferSlaData } from 'redux/slices/sla-reports/transfer-sla';
import DublinScorecardTable from './dublin-scorecard-table';
import DublinScorecardDailyTable from './dublin-scorecard-daily-table';
import DublinSlaKpiReportTable from './dublin-sla-kpi-report-table';

const SUB_REPORTS = [
    {
        key: 'ecac',
        summaryKey: 'ecac',
        label: 'Terminal Reception',
        thunk: fetchECACReport,
        reset: resetECACReport,
        buildParams: (base) => ({ ...base, format: 'kpi', hideBreakdown: true }),
    },
    {
        key: 'external-call-point',
        summaryKey: 'externalCallPoint',
        label: 'External Call Point',
        thunk: fetchExternalCallPointReport,
        reset: resetExternalCallPointReport,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'boarding-sla',
        summaryKey: 'boardingSla',
        label: 'Boardings',
        thunk: fetchBoardingSla,
        reset: resetBoardingSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'inbound-sla',
        summaryKey: 'inboundSla',
        label: 'Inbounds',
        thunk: fetchInboundSla,
        reset: resetInboundSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'terminal-reception',
        summaryKey: 'terminalReception',
        label: 'Terminal Holding Points (218/407)',
        thunk: fetchTerminalReceptionReport,
        reset: resetTerminalReception,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'transfer-sla',
        summaryKey: 'transferSla',
        label: 'Transfer SLA',
        thunk: fetchTransferSla,
        reset: resetTransferSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
];

// The sub-reports are fetched STRICTLY ONE AT A TIME. Do not parallelise this.
// Tried it (3 concurrent) and it made things worse, not faster: each report's
// per-day getFlights call has a 60s axios timeout (REPORT_FLIGHTS_TIMEOUT_MS in
// sla-reports.js), and overlapping reports saturate prm-cpu / prm-flights
// enough that those individual calls blow past 60s. Aug 2026 came back as
// "External Call Point report failed | 238985ms elapsed | timeout of 60000ms
// exceeded" — a whole report lost, where sequentially it merely took a while.
const VIEWS = [
    { key: 'sla', label: 'PRM SLA Performance Report' },
    { key: 'daily', label: 'Daily KPI Breakdown' },
    { key: 'scorecard', label: 'Scorecard (range total)' },
];

const toLocalDateString = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const DublinScorecardReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
    const dispatch = useDispatch();
    const cancelledRef = useRef(false);

    // DAA's PRM SLA Performance Report (Aug-26 workbook scoring) is the
    // primary view; the daily matrix and pooled scorecard stay behind the toggle.
    const [view, setView] = useState('sla');
    // the range the data was fetched for — the picker can change after a run
    const [ranRange, setRanRange] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [errors, setErrors] = useState({});
    const [done, setDone] = useState(false);
    const [completedKeys, setCompletedKeys] = useState({});

    const ecacData = useSelector(selectECACReportData);
    const extCallPointData = useSelector(selectExternalCallPointReportData);
    const boardingData = useSelector(selectBoardingSlaData);
    const inboundData = useSelector(selectInboundSlaData);
    const termReceptionData = useSelector(selectTerminalReceptionData);
    const transferData = useSelector(selectTransferSlaData);

    const isRunning = currentIndex !== null;

    const handleRunReport = useCallback(async () => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];
        if (!startDate || !endDate || !iata) return;

        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);
        const baseParams = { startDate: formattedStartDate, endDate: formattedEndDate, iata };

        cancelledRef.current = false;
        setErrors({});
        setDone(false);
        setCompletedKeys({});
        setRanRange({ startDate: formattedStartDate, endDate: formattedEndDate });

        for (let i = 0; i < SUB_REPORTS.length; i++) {
            if (cancelledRef.current) return;
            setCurrentIndex(i);
            const report = SUB_REPORTS[i];
            try {
                await dispatch(report.thunk(report.buildParams(baseParams))).unwrap();
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            } catch (e) {
                setErrors((prev) => ({ ...prev, [report.key]: e?.message ?? 'Failed' }));
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            }
        }

        if (!cancelledRef.current) {
            setCurrentIndex(null);
            setDone(true);
        }
    }, [dispatch, dateRange, iata]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isRunning);
    }, [isRunning, onRunLoadingChange]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            SUB_REPORTS.forEach((r) => dispatch(r.reset()));
        };
    }, [dispatch]);

    const progressPercent = isRunning
        ? Math.round(((currentIndex + 1) / SUB_REPORTS.length) * 100)
        : done ? 100 : 0;

    const summaries = {
        ecac: ecacData?.summary ?? null,
        externalCallPoint: extCallPointData?.summary ?? null,
        boardingSla: boardingData?.summary ?? null,
        inboundSla: inboundData?.summary ?? null,
        terminalReception: termReceptionData?.summary ?? null,
        transferSla: transferData?.summary ?? null,
    };

    const daily = {
        ecac: ecacData?.dailyReports ?? [],
        externalCallPoint: extCallPointData?.dailyReports ?? [],
        boardingSla: boardingData?.dailyReports ?? [],
        inboundSla: inboundData?.dailyReports ?? [],
        terminalReception: termReceptionData?.dailyReports ?? [],
        transferSla: transferData?.dailyReports ?? [],
    };

    const errorsBySummaryKey = useMemo(() => {
        const out = {};
        SUB_REPORTS.forEach((r) => { if (errors[r.key]) out[r.summaryKey] = errors[r.key]; });
        return out;
    }, [errors]);

    const allFailed = done && SUB_REPORTS.every((r) => errors[r.key]);

    return (
        <div>
            {(isRunning || (!done && progressPercent === 0)) && (
                <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    backgroundColor: '#f9fafb',
                }}>
                    {isRunning && (
                        <>
                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', marginTop: 0, marginBottom: '12px' }}>
                                Fetching report {currentIndex + 1} of {SUB_REPORTS.length}: {SUB_REPORTS[currentIndex].label}
                            </p>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                }} />
                            </div>
                        </>
                    )}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUB_REPORTS.map((report, idx) => {
                            let status = 'pending';
                            if (errors[report.key]) status = 'error';
                            else if (completedKeys[report.key]) status = 'done';
                            else if (isRunning && idx === currentIndex) status = 'loading';

                            const icon = {
                                pending: '\u25CB',
                                loading: '\u25CF',
                                done: '\u2713',
                                error: '\u2717',
                            }[status];

                            const color = {
                                pending: '#9ca3af',
                                loading: '#3b82f6',
                                done: '#16a34a',
                                error: '#dc2626',
                            }[status];

                            return (
                                <li key={report.key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0',
                                    fontSize: '13px',
                                    color,
                                    fontWeight: status === 'loading' ? 600 : 400,
                                }}>
                                    <span style={{ fontSize: '14px', width: '16px', textAlign: 'center' }}>{icon}</span>
                                    {report.label}
                                    {errors[report.key] && (
                                        <span style={{ fontSize: '12px', color: '#dc2626', marginLeft: '4px' }}>
                                            — {errors[report.key]}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {done && (
                <>
                    {allFailed ? (
                        <p style={{ fontSize: '13px', color: '#dc2626' }}>
                            Failed to load all sub-reports.
                        </p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                                {VIEWS.map((v) => (
                                    <button
                                        key={v.key}
                                        type="button"
                                        onClick={() => setView(v.key)}
                                        style={{
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            fontWeight: view === v.key ? 700 : 400,
                                            cursor: 'pointer',
                                            backgroundColor: view === v.key ? '#1e3a5f' : '#f3f4f6',
                                            color: view === v.key ? '#fff' : '#374151',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                        }}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                            {view === 'sla' && (
                                <DublinSlaKpiReportTable
                                    daily={daily}
                                    errors={errorsBySummaryKey}
                                    startDate={ranRange?.startDate}
                                    endDate={ranRange?.endDate}
                                />
                            )}
                            {view === 'daily' && (
                                <DublinScorecardDailyTable daily={daily} summaries={summaries} errors={errors} />
                            )}
                            {view === 'scorecard' && (
                                <DublinScorecardTable summaries={summaries} errors={errors} />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default DublinScorecardReport;
